import { useState, useCallback, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { fetchRecordedEntryPath } from '@/lib/auth/entryPath';
import { toast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { CheckCircle2, Circle, Loader2, ShieldCheck } from 'lucide-react';
import { PRIVACY_CONTENT, PRIVACY_TITLE } from '@/legal/privacy';
import { SHOP_TOS_CONTENT, SHOP_TOS_TITLE } from '@/legal/tos_shop';
import { PASSPORT_TOS_CONTENT, PASSPORT_TOS_TITLE } from '@/legal/tos_passport';
import {
  CURRENT_PRIVACY_VERSION,
  CURRENT_SHOP_TOS_VERSION,
  CURRENT_PASSPORT_TOS_VERSION,
  CURRENT_AGE_VERIFICATION_VERSION,
} from '@/legal/versions';

const SCROLL_THRESHOLD = 40; // px from bottom to count as "scrolled to bottom"

const BetaLegalReview = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Scroll tracking
  const [privacyScrolled, setPrivacyScrolled] = useState(false);
  const [tosScrolled, setTosScrolled] = useState(false);

  // Checkboxes
  const [ageChecked, setAgeChecked] = useState(false);
  const [agreeChecked, setAgreeChecked] = useState(false);

  // ── A LEGAL STEP STARTS AT THE TOP ────────────────────────────────────
  //
  // Arriving here from the signed agreement, the router preserved the previous
  // step's OUTER scroll offset, so the reader landed part-way down a document
  // they had never seen. Two different scrolls have to be reset, and they are
  // not the same thing: the page itself, and each document pane.
  //
  // A callback ref does the pane, because Radix unmounts the inactive
  // TabsContent — the ToS pane does not exist at first paint, and gets its
  // reset when the tab is opened. This only ever scrolls a pane to the TOP, so
  // it cannot manufacture the bottom-of-document state that gates acceptance.
  const resetPaneToTop = useCallback((el: HTMLDivElement | null) => {
    if (el) el.scrollTop = 0;
  }, []);

  useLayoutEffect(() => {
    // Before paint, so the reader never sees the inherited offset.
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  // Core-100 R2 (CORE-A): the entry path as the server recorded it at sign-up (immutable), which decides
  // the terms that apply — not the user-editable metadata.
  const { data: recordedEntryPath } = useQuery({
    queryKey: ['recorded-entry-path', user?.id],
    queryFn: () => fetchRecordedEntryPath(user!.id),
    enabled: !!user?.id,
  });
  const accountType: 'shop' | 'passport' = recordedEntryPath ?? 'shop';

  const tosContent = accountType === 'passport' ? PASSPORT_TOS_CONTENT : SHOP_TOS_CONTENT;
  const tosTitle = accountType === 'passport' ? PASSPORT_TOS_TITLE : SHOP_TOS_TITLE;
  const tosType = accountType === 'passport' ? 'tos_passport' : 'tos_shop';
  const tosVersion = accountType === 'passport' ? CURRENT_PASSPORT_TOS_VERSION : CURRENT_SHOP_TOS_VERSION;

  const canSubmit = privacyScrolled && tosScrolled && ageChecked && agreeChecked && !isSubmitting;

  const handleScroll = useCallback(
    (setter: (v: boolean) => void) => (e: React.UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= SCROLL_THRESHOLD;
      if (atBottom) setter(true);
    },
    []
  );

  const handleSubmit = async () => {
    if (!canSubmit || !user?.id) return;
    setIsSubmitting(true);

    try {
      const ua = navigator.userAgent || 'unknown';
      const acceptances = [
        {
          user_id: user.id,
          agreement_type: 'privacy_policy',
          agreement_version: CURRENT_PRIVACY_VERSION,
          user_agent: ua,
        },
        {
          user_id: user.id,
          agreement_type: tosType,
          agreement_version: tosVersion,
          user_agent: ua,
        },
        {
          user_id: user.id,
          agreement_type: 'age_verification',
          agreement_version: CURRENT_AGE_VERIFICATION_VERSION,
          user_agent: ua,
        },
      ];

      const { error } = await supabase
        .from('legal_acceptances')
        .upsert(acceptances, {
          onConflict: 'user_id,agreement_type,agreement_version',
          ignoreDuplicates: true,
        });

      if (error) throw error;

      await queryClient.invalidateQueries({ queryKey: ['beta-signing-gate'] });
      await queryClient.refetchQueries({ queryKey: ['beta-signing-gate'] });

      toast({
        title: 'Legal Review Complete',
        description: 'You have accepted the Privacy Policy and Terms of Service.',
      });

      localStorage.setItem('startTutorialOnLoad', 'true');
      // Route to the first-login chooser (FirstLoginJunction →
      // Import Shop History OR Manual Quick Setup → LeanSetupWizard).
      // Previously this dropped beta users on the Dayboard with zero
      // techs / bays / parking — the wizard was built and wired for
      // manual shop creation (OnboardShop.tsx:125-130) but the beta
      // invite path never hit /onboard/setup because provision-beta-org
      // creates the shop server-side without the OnboardShop redirect.
      //
      // LeanSetupWizard.commitWizard navigates to '/' on completion
      // (see src/lib/onboarding/commitWizard.ts + LeanSetupWizard.tsx),
      // so the eventual Dayboard arrival still happens — just AFTER
      // the wizard or import flow has populated the shop's people /
      // bays / parking. OnboardSetup itself short-circuits to /onboard
      // if the user has no active shop, so this is safe for the rare
      // race where activeShop hasn't propagated yet.
      //
      // Billing setup (Stripe SetupIntent for the 60d → $174.50 →
      // $349 schedule) is intentionally deferred to a later batch —
      // see the 2026-05-27 audit. The existing `billing_enabled_at`
      // gate on the shop already enforces that beta shops cannot
      // invoke stripe-create-checkout until an admin enables billing.
      navigate('/onboard/setup');
    } catch (err: any) {
      console.error('Legal review submission error:', err);
      toast({
        title: 'Submission Failed',
        description: err.message || 'Could not record your acceptance. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <ShieldCheck className="h-10 w-10 mx-auto text-primary" />
          <h1 className="text-2xl font-bold">Review Legal Agreements</h1>
          <p className="text-muted-foreground">
            Please read each document in full before accepting.
          </p>
        </div>

        {/* Scroll status indicators */}
        <div className="flex items-center justify-center gap-6 text-sm">
          <span className="flex items-center gap-1.5">
            {privacyScrolled ? (
              <CheckCircle2 className="h-4 w-4 text-green-600" />
            ) : (
              <Circle className="h-4 w-4 text-muted-foreground" />
            )}
            Privacy Policy
          </span>
          <span className="flex items-center gap-1.5">
            {tosScrolled ? (
              <CheckCircle2 className="h-4 w-4 text-green-600" />
            ) : (
              <Circle className="h-4 w-4 text-muted-foreground" />
            )}
            Terms of Service
          </span>
        </div>

        {/* Document Tabs */}
        <Card>
          <CardContent className="pt-6">
            <Tabs defaultValue="privacy">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="privacy" className="flex items-center gap-1.5">
                  {privacyScrolled && <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />}
                  {PRIVACY_TITLE}
                </TabsTrigger>
                <TabsTrigger value="tos" className="flex items-center gap-1.5">
                  {tosScrolled && <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />}
                  {tosTitle}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="privacy" className="mt-4">
                <div
                  ref={resetPaneToTop}
                  className="h-80 overflow-y-auto rounded-md border p-4"
                  onScroll={handleScroll(setPrivacyScrolled)}
                >
                  <pre className="whitespace-pre-wrap text-sm text-foreground font-sans leading-relaxed">
                    {PRIVACY_CONTENT}
                  </pre>
                </div>
                {!privacyScrolled && (
                  <p className="text-xs text-muted-foreground mt-2 text-center">
                    Scroll to the bottom to continue
                  </p>
                )}
              </TabsContent>

              <TabsContent value="tos" className="mt-4">
                <div
                  ref={resetPaneToTop}
                  className="h-80 overflow-y-auto rounded-md border p-4"
                  onScroll={handleScroll(setTosScrolled)}
                >
                  <pre className="whitespace-pre-wrap text-sm text-foreground font-sans leading-relaxed">
                    {tosContent}
                  </pre>
                </div>
                {!tosScrolled && (
                  <p className="text-xs text-muted-foreground mt-2 text-center">
                    Scroll to the bottom to continue
                  </p>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Acceptance Area */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Acceptance</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-2">
              <Checkbox
                id="age-verify"
                checked={ageChecked}
                onCheckedChange={(v) => setAgeChecked(v === true)}
              />
              <Label htmlFor="age-verify" className="text-sm leading-relaxed cursor-pointer">
                I confirm I am at least 18 years of age
              </Label>
            </div>

            <div className="flex items-start gap-2">
              <Checkbox
                id="agree-legal"
                checked={agreeChecked}
                onCheckedChange={(v) => setAgreeChecked(v === true)}
                disabled={!privacyScrolled || !tosScrolled}
              />
              <Label
                htmlFor="agree-legal"
                className={`text-sm leading-relaxed cursor-pointer ${
                  !privacyScrolled || !tosScrolled ? 'text-muted-foreground' : ''
                }`}
              >
                I have read and agree to the Privacy Policy and Terms of Service
              </Label>
            </div>

            {(!privacyScrolled || !tosScrolled) && (
              <p className="text-xs text-muted-foreground">
                You must scroll to the bottom of both documents before you can accept.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex justify-end">
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Continue
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BetaLegalReview;
